import { useState, VFC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  IconButton,
  Menu,
  MenuItem,
} from '@material-ui/core';
import {
  addProductToCart, subtractProductFromCart,
} from '../../reducks/users/operations';
import { FirebaseTimestamp } from '../../firebase';
import HTMLReactParser from 'html-react-parser';
import { makeStyles } from '@material-ui/core/styles';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { ListItem, ListItemText } from '@material-ui/core';
import AddCircle from '@material-ui/icons/AddCircle';
import RemoveCircle from '@material-ui/icons/RemoveCircle';
import { deleteProduct, hiddenProduct, showProduct } from '../../reducks/products/operations';
import { RootState } from '../../reducks/store/store';
import { getRole } from '../../reducks/users/selectors';
import { getProductsInCart } from '../../reducks/users/selectors';
import { ProductInCart } from '../../reducks/users/types';
import { Product } from "../../reducks/products/types";
import { useNavigate } from 'react-router-dom';
import {getYYYYMMDD} from "../../utils/dateUtils";

type Props = {
  product: Product;
  receiveDate: Date;
  daysDiff?: number;
};

const returnCodeToBr = (text: string) => {
  if (text === '') {
    return text;
  } else {
    return HTMLReactParser(text.replace(/\r?\n/g, '<br />'));
  }
};

const useStyles = makeStyles((theme) => ({
  root: {
    [theme.breakpoints.down('sm')]: {
      margin: 8,
      width: 'calc(50% - 16px)',
    },
    [theme.breakpoints.up('sm')]: {
      margin: 16,
      width: 'calc(33.3333% - 32px)',
    },
  },
  content: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '16px 8px',
    textAlign: 'left',
    '&:lastchild': {
      paddingBottom: 16,
    },
  },
  media: {
    height: '0',
    paddingTop: '100%',
    cursor: 'pointer',
    position: 'relative',
  },
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(128, 128, 128, 0.7)', // Semi-transparent gray color
  },
  soldOut: {
    filter: 'grayscale(100%)',
  },
  price: {
    color: 'brown',
    fontSize: 16,
  },
  quantity:{
    width: '100%',
    textAlign: 'center',
  },
  quantityText: {
    width: '50px',
    textAlign: 'center',
  },
}));

export const ProductCard: VFC<Props> = (props) => {
  const { product, receiveDate } = props;

  const classes = useStyles();
  const dispatch = useDispatch();
  const selector = useSelector((state: RootState) => state);
  const navigate = useNavigate();

  const role = getRole(selector);
  const cart = getProductsInCart(selector);
  const quantity = cart.find((productInCart: ProductInCart) => (productInCart.id === product.id))?.quantity ?? 0;

  const yyyymmdd = getYYYYMMDD(receiveDate);


  // メニュー開閉する対象の要素
  const [anchorEl, setAnchorEl] = useState<
    Element | ((element: Element) => Element) | null | undefined
  >(null);

  /**
   * メニューを開く.
   *
   * @param e - event
   */
  const handleClick = (e: any) => {
    setAnchorEl(e.currentTarget);
  };

  /**
   *  メニューを閉じる
   */
  const handleClose = () => {
    setAnchorEl(null);
  };
  // 商品画像。商品画像がない場合はNoImageを表示する
  const image = product.images.length > 0 ? product.images[0].path : '/img/no_image.png';

  // 商品の価格
  const price = product.price.toLocaleString();

  const addProductQuantity = async () => {
    const timestamp = FirebaseTimestamp.now();

    if(!product.stocks[yyyymmdd])return;

    if(!product.ordered || !product.ordered[yyyymmdd]){
      if(product.stocks[yyyymmdd] - quantity <= 0) return;
    }else{
        if(product.stocks[yyyymmdd] - product.ordered[yyyymmdd] - quantity <= 0) return;
    }

    dispatch(
      addProductToCart(
        {
          ...product,
          added_at: timestamp,
        },
        cart
      )
    );
  };

  const subtractProductQuantity = async () => {
    const timestamp = FirebaseTimestamp.now();

    dispatch(
      subtractProductFromCart(
        {
          ...product,
          added_at: timestamp,
        },
        cart
      )
    );
  };


  return (
    <Card className={classes.root}>
      <CardMedia
        className={classes.media}
        image={image}
        onClick={() => navigate('/product/detail/' + product.id)}
      >
        {(((product.stocks[yyyymmdd] - product.ordered[yyyymmdd] <= 0 || !product.stocks[yyyymmdd]) && role !== 'admin') ||
          (role === 'admin' && product.status === 'hidden')
          ) && <div className={classes.overlay} />}
      </CardMedia>
      <CardContent className={classes.content}>
        <div>
          <Typography color='textSecondary' component='p'>
            {returnCodeToBr(product.name)}
          </Typography>
          <Typography className={classes.price} component='p'>
            ¥{price}
          </Typography>
        </div>
        {role === 'admin' && (
          <>
            <IconButton onClick={handleClick}>
              <MoreVertIcon />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              keepMounted
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              <MenuItem
                onClick={() => navigate('/product/edit/' + product.id)}
              >
                編集する
              </MenuItem>
              {product?.status === "hidden" ?
                <MenuItem onClick={() => dispatch(showProduct(product.id))}>
                  表示にする
                </MenuItem> :
                <MenuItem onClick={() => dispatch(hiddenProduct(product.id))}>
                  非表示にする
                </MenuItem>
              }
              <MenuItem 
                onClick={() => {
                  if (window.confirm('本当にこの商品を削除しますか？')) {
                    dispatch(deleteProduct(product.id));
                    handleClose();
                  }
                }}
                style={{ color: 'red' }}
              >
                削除する
              </MenuItem>
            </Menu>
          </>
        )}
      </CardContent>
      {role === 'customer' && (
        <div className={classes.quantity}>
          {(product.stocks[yyyymmdd] - product.ordered[yyyymmdd] <= 0 || !product.stocks[yyyymmdd]) ? (
            <Typography color="error">在庫なし</Typography>
          ) : (
            <div>
            <ListItem>
              <IconButton onClick={subtractProductQuantity}>
                <RemoveCircle />
              </IconButton>
              <div className={classes.quantityText}>
                <ListItemText
                  primary={`${quantity}`}
                />
              </div>
              <IconButton onClick={addProductQuantity}>
                <AddCircle />
              </IconButton>
            </ListItem>
            {product.stocks[yyyymmdd] - product.ordered[yyyymmdd] <= 5 ? <Typography color="error">{"残り："+(product.stocks[yyyymmdd] - product.ordered[yyyymmdd])+"点"}</Typography> : <></>}
            </div>
          )}
        </div>
      )}
      {role === 'admin' && (
        <>
          {(!product.stocks[yyyymmdd] ||
            (!product.ordered[yyyymmdd] && product.stocks[yyyymmdd] > 5) ||
            product.stocks[yyyymmdd] - product.ordered[yyyymmdd] > 5) ?
          (
            <>
              <Typography>予約数：{product.ordered[yyyymmdd] ?? "0"}</Typography><p/>
              <Typography>製造予定数：{product.stocks[yyyymmdd] ?? "0"}</Typography><p/>
              <div>{product.status === 'hidden' ? "非表示" : "表示"}</div>
            </>
          ):(
            <>
              <Typography color='error'>予約数：{product.ordered[yyyymmdd] ?? "0"}</Typography><p/>
              <Typography color='error'>製造予定数：{product.stocks[yyyymmdd] ?? "0"}</Typography><p/>
              <div>{product.status === 'hidden' ? "非表示" : "表示"}</div>
            </>
          )}
        </>
      )}
    </Card>
  );
};
